import React, { useCallback, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { fr, enUS } from 'date-fns/locale';

import styles from './style';

// The mask of the picker adds the "/" right after the 2nd digit but leaves the cursor before it.
// Here the "/" comes with the next digit: the cursor always stays at the end of what is typed.
// dd/MM/yyyy and MM/dd/yyyy have the same 2/2/4 parts.
const formatDateInput = (text: string) => {
  const digits = text.replace(/\D/g, '').slice(0, 8);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join('/');
};

interface DatePickProps {
  control: any;
  // Dark style of the connected pages and the auth pages.
  dark?: boolean;
  language: string;
  label?: string;
  name: string;
  maxDate: Date | null;
  value?: Date | null,
}

const DatePick: React.FC<DatePickProps> = React.memo((props) => {
  const {
    control,
    dark,
    label,
    language,
    name,
    maxDate,
    value,
  } = props;
  const { field } = useController({ control, name });
  const { value: fieldValue, onChange: onChangeDate } = field;

  const locale = useMemo(() => {
    if (language === 'fr') {
      return fr;
    }
    return enUS;
  }, [language]);

  const date = useMemo(() => {
    if (!fieldValue) {
      return value;
    }
    return fieldValue;
  }, [fieldValue, value]);

  const textFieldSx = useMemo(() => {
    if (dark) {
      return styles.textFieldDark;
    }
    return styles.textField;
  }, [dark]);

  const renderInput = useCallback((params: TextFieldProps) => {
    return <TextField {...params} sx={textFieldSx} />;
  }, [textFieldSx]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      locale={locale}
    >
      <DatePicker
        maxDate={maxDate}
        disableFuture
        label={label}
        value={date}
        onChange={onChangeDate}
        rifmFormatter={formatDateInput}
        renderInput={renderInput}
      />
    </LocalizationProvider>
  );
});

export default DatePick;
